
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type WaitlistInsert = Database['public']['Tables']['waitlist']['Insert'];

export interface WaitlistFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  gdprConsent: boolean;
  noSpamPromise: boolean;
  communicationPreferences?: {
    email: boolean;
    sms: boolean;
  };
  source?: string;
}

export const useWaitlistForm = (isPro: boolean, onSuccess: () => void) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<WaitlistFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    gdprConsent: false,
    noSpamPromise: false,
    communicationPreferences: {
      email: true,
      sms: false
    },
    source: typeof window !== 'undefined' ? window.location.pathname : '/',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Vérification de l'email existant
      const { data: existingUser } = await supabase
        .from("waitlist")
        .select('status')
        .eq('email', formData.email.toLowerCase().trim())
        .maybeSingle();

      if (existingUser && existingUser.status !== 'cancelled') {
        setIsLoading(false);
        toast({
          variant: "destructive",
          title: "Email déjà inscrit",
          description: "Cette adresse email est déjà inscrite sur notre liste d'attente.",
        });
        return;
      }

      const insertData: WaitlistInsert = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone || null,
        company: formData.company || null,
        is_pro: isPro,
        gdpr_consent: formData.gdprConsent,
        status: 'pending',
        communication_preferences: formData.communicationPreferences,
        source: formData.source,
        metadata: {
          browser: navigator.userAgent,
          language: navigator.language,
          timestamp: new Date().toISOString(),
        }
      };

      const { error: dbError } = await supabase
        .from("waitlist")
        .insert(insertData);

      if (dbError) throw dbError;

      try {
        const { error: emailError } = await supabase.functions.invoke("send-waitlist-notification", {
          body: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            isPro,
            source: formData.source,
          },
        });

        if (emailError) {
          console.error("[useWaitlistForm] Erreur d'envoi de l'email:", emailError);
          toast({
            variant: "default",
            title: "Inscription réussie !",
            description: "Nous vous contacterons très bientôt. Un problème technique nous empêche de vous envoyer l'email de confirmation pour le moment.",
          });
        } else {
          await supabase
            .from("waitlist")
            .update({ notification_sent: true })
            .eq('email', formData.email.toLowerCase().trim());

          toast({
            title: "Inscription réussie !",
            description: "Nous vous contacterons très bientôt.",
          });
        }
      } catch (emailError) {
        console.error("[useWaitlistForm] Erreur lors de l'envoi de l'email:", emailError);
        toast({
          variant: "default",
          title: "Inscription réussie !",
          description: "Nous vous contacterons très bientôt. Un problème technique nous empêche de vous envoyer l'email de confirmation pour le moment.",
        });
      }

      onSuccess();
    } catch (error) {
      console.error("[useWaitlistForm] Erreur lors de la soumission:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    isLoading,
    handleSubmit,
  };
};
